import { DocsAccessResponseV1 } from "@strust/contracts";
import { useMutation } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { useApiClient } from "../../../shared/api/api-provider.tsx";
import { defineEndpoint } from "../../../shared/api/endpoint.ts";
import { saveBlob } from "../../../shared/save-blob.ts";

export const docsAccessEndpoint = defineEndpoint({
  method: "GET",
  path: "/docs-access",
  response: DocsAccessResponseV1,
});

/** The two languages of the docs site: English at its root, Brazilian Portuguese under `/pt-BR/`. */
export type DocsLocale = "en" | "pt-BR";

/** The docs language for an Explorer locale (Portuguese for any `pt*`, English otherwise). */
export function docsLocaleOf(locale: string): DocsLocale {
  return locale.toLowerCase().startsWith("pt") ? "pt-BR" : "en";
}

/**
 * The docs site serves English at its root and Brazilian Portuguese under `/pt-BR/` (Docusaurus
 * i18n), so the console opens the locale the user reads it in.
 */
export function docsUrlForLocale(docsUrl: string, locale: string): string {
  const url = new URL(docsUrl);
  if (docsLocaleOf(locale) === "pt-BR") {
    url.pathname = `${url.pathname.replace(/\/$/, "")}/pt-BR/`;
  }
  return url.toString();
}

/**
 * The `llms-full.txt` of the user's language: the file name joined to the docs URL as a URL, so a
 * `docs_url` with or without its trailing slash (`…/docs` or `…/docs/`) gives the same address
 * (design D6 of add-integration-docs-llms-txt).
 */
export function llmsFullUrlForLocale(docsUrl: string, locale: string): string {
  const base = new URL(docsUrlForLocale(docsUrl, locale));
  if (!base.pathname.endsWith("/")) base.pathname = `${base.pathname}/`;
  return new URL("llms-full.txt", base).toString();
}

/** The name the downloaded file is saved under; it carries the locale, so both files can be kept. */
export function llmsFullFilename(locale: string): string {
  return `strust-llms-full.${docsLocaleOf(locale)}.txt`;
}

/** The docs server refused or failed the file request (`status` 0: the request never completed). */
export class DocsDownloadError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`docs_download_failed_${String(status)}`);
    this.name = "DocsDownloadError";
    this.status = status;
  }
}

export interface DownloadLlmsFullDeps {
  /** `GET /explorer/v1/docs-access`: the grant; its signed cookies arrive as `Set-Cookie`. */
  readonly requestGrant: () => Promise<DocsAccessResponseV1>;
  readonly fetch: typeof globalThis.fetch;
  readonly save: (blob: Blob, filename: string) => void;
  readonly locale: string;
}

/**
 * Downloads the `llms-full.txt` of the user's language through the docs access grant (spec
 * integration-docs, "[FR] Documentation download for AI agents"): the grant first, then the file
 * with the signed cookies. A `403` means the docs cookie expired while the console session lives,
 * so the grant is asked for once more and the file once more; anything else that is not a `200`,
 * or a second failure, is an error and nothing is saved. The bytes are saved as they were served.
 */
export async function downloadLlmsFull(deps: DownloadLlmsFullDeps): Promise<void> {
  const fetchFile = async (): Promise<Response> => {
    const grant = await deps.requestGrant();
    try {
      return await deps.fetch(llmsFullUrlForLocale(grant.docs_url, deps.locale), {
        credentials: "include",
      });
    } catch {
      throw new DocsDownloadError(0);
    }
  };
  let response = await fetchFile();
  if (response.status === 403) response = await fetchFile();
  if (response.status !== 200) throw new DocsDownloadError(response.status);
  deps.save(await response.blob(), llmsFullFilename(deps.locale));
}

/**
 * "Download llms-full.txt" of the Apps menu: the console stays on its screen. A grant refused with
 * `unauthenticated` already sends the user to login through the API client.
 */
export function useDownloadLlmsFull() {
  const client = useApiClient();
  const { locale } = useIntl();
  return useMutation({
    mutationFn: () =>
      downloadLlmsFull({
        requestGrant: () => client.call(docsAccessEndpoint, {}),
        fetch: (...args) => globalThis.fetch(...args),
        save: saveBlob,
        locale,
      }),
  });
}

/**
 * Asks the API for the integration docs grant (CloudFront signed cookies arrive as `Set-Cookie`)
 * and opens `docs_url` in a new tab, detached from the console (`noopener`).
 */
export function useOpenDocs() {
  const client = useApiClient();
  const { locale } = useIntl();
  return useMutation({
    mutationFn: () => client.call(docsAccessEndpoint, {}),
    onSuccess: (grant) => {
      window.open(docsUrlForLocale(grant.docs_url, locale), "_blank", "noopener,noreferrer");
    },
  });
}
